<script setup lang="ts">
import { getAppsByTypeApi } from '~/api'
import type { Page, ReturnPromiseType } from '~/types/base'

definePageMeta({
  validate: (route) => {
    if (route.params.type === 'upcoming-games') {
      return true
    }
    if (!Number.isFinite(+route.params.type)) {
      return createError({ statusCode: 404, statusMessage: 'game type is not a number' })
    }
    else {
      return true
    }
  },
})

const { t } = useI18n()
const route = useRoute()
// 接口参数
const page = computed<Page>(() => ({
  size: 40,
  current: +(route.query.current as string) || 1,
}))

const type = computed(() => route.params.type as string)
const pageKey = computed(() => `app-type-${type.value}-${page.value.current}`)

const { value, addToCache } = await useDataCache<ReturnPromiseType<typeof getAppsByTypeApi>>(unref(pageKey))
const { data, status, error } = await useAsyncData(unref(pageKey), async () => {
  if (value) {
    return value
  }

  const response = await getAppsByTypeApi(type.value, page.value)
  addToCache(response)
  return response
}, {
  getCachedData: (key, nuxt): ReturnPromiseType<typeof getAppsByTypeApi> => nuxt.payload.data[key] || nuxt.static.data[key],
  lazy: import.meta.client,
  watch: [() => route.query],
})
whenever(error, () => {
  useRouteCache(route => route.setUncacheable())
  throw createError({ statusCode: 404, statusMessage: `${unref(pageKey)} has some error`, data: { type: type.value }, fatal: true })
}, { immediate: true })

if (!isProd()) {
  whenever(data, v => clientLog.log(unref(pageKey), v), { immediate: true })
}

const games = computed(() => data.value?.games?.records || [])
const pages = computed(() => data.value?.games?.pages || 1)
const typeName = computed(() => type.value === 'upcoming-games' ? t('game.upcoming_games') : data.value?.typeName)
const { toAppDetail } = useRouterToPage()

const { autoSideBar } = useLayout()
autoSideBar()

const { appTypeHead } = usePageHead()
appTypeHead(typeName.value)

// useCacheRoute()
</script>

<template>
  <div v-track="{ id: 'web-apps-type', view: { value: { page: 'apps-type', seo: type } } }" class="view-content" pb="80px md:160px">
    <h1 class="text-center c-#161636 font-900" lt-md="mt-40px text-20px" md="mt-60px text-32px">
      <Icon :class="data ? 'op-100' : 'op-0'" class="v-bottom" lt-md="text-24px mie-8px" md="mie-12px" name="SearchHash" />
      <span>{{ typeName }}</span>
    </h1>

    <div
      class="grid"
      lt-md="grid-cols-4 gap-[20px_12px] mt-40px"
      md="grid-cols-8 gap-[24px_40px] mt-60px"
    >
      <template v-if="status === 'pending'">
        <GameLiteBlockSkeleton v-for="i in 32" :key="i" />
      </template>
      <template v-else>
        <template v-for="item in games" :key="item.gameId">
          <GameLiteBlock
            v-track="{ id: 'web-apps-type-game', click: { value: { type: typeName, name: item.gameName, seo: item.seo, pkg: item.packageName } } }"
            mode="light" :name="item.gameName" :icon="item.icon"
            :to="toAppDetail({ seo: item.seo, type: item.category }, { hasReturnUrl: true })"
          />
        </template>
      </template>
    </div>

    <LdPagination v-if="data" v-model="page.current" class="mt-40px" :pages="pages" scroll-to-top />
  </div>
</template>

<style lang="scss" scoped>
</style>
